import { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Grid, IconButton, Typography, Box, Avatar, LinearProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Snackbar, Alert, Divider, MenuItem
} from '@mui/material';
import {
    Close as CloseIcon,
    AssignmentReturn as DeliveryInIcon,
    Receipt as InvoiceIcon
} from '@mui/icons-material';
import { updateData, fetchData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const getCurrentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

const today = () => new Date().toISOString().split('T')[0];

const emptyDelivery = {
    Id: '', CompanyId: '', BranchId: '',
    CustomerId: '', CustomerName: '', ContactNumber: '', AddressLine: '',
    DeliveryDate: '', DeliveryReturnDate: today(), Remarks: '',
    BillNumber: '', DeliveryNo: '', FinYear: '',
    RentDays: 0, DeliveryOutTime: '', DeliveryInTime: getCurrentTime(),
    IsDeliveryInCompleted: false, IsInvoiced: false,
    items: []
};

const emptyInvoice = {
    Id: '', DeliveryId: '', CustomerId: '', CustomerName: '', ContactNumber: '', AddressLine: '',
    BillNumber: '', BillPrefix: '', BillNo: '', FinYear: '', BillDate: new Date().toISOString().split('T')[0],
    Amount: 0, DiscountAmount: 0, NetAmount: 0,
    RoundOff: 0, TransportingCharge: 0, TotalAmount: 0,
    AdvanceAmount: 0, PaidAmount: 0, BalanceAmount: 0,
    ModeOfPayment: 0, Remarks: '', RentDays: 0,
    AdditionalAmount: 0, AdditionalRemarks: '',
    items: []
};

const DeliveryInModal = ({ open, onClose, onSave, deliveryData = null, mode = 'edit' }) => {
    const { companyId, branchId, token, userId } = useAuth();
    const isView = mode === 'view';

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showError, setShowError] = useState(false);
    const [errors, setErrors] = useState({});

    const [delivery, setDelivery] = useState({ ...emptyDelivery });
    const [invoice, setInvoice] = useState({ ...emptyInvoice });
    const [rawD, setRawD] = useState(null);
    const [rawDets, setRawDets] = useState([]);

    useEffect(() => {
        if (!open) return;
        if (deliveryData?.Id) {
            loadDeliveryData(deliveryData.Id);
        }
        fetchRentInvoiceBillNumber();
    }, [open, deliveryData]);

    const fetchRentInvoiceBillNumber = async () => {
        try {
            const res = await fetchData('RentInvoiceApi', `/MaxBillno/${companyId}/${branchId}`, token);
            const data = res?.response_data || res || {};
            const billNumber = data.BillNumber || data.billNumber || 1;
            const billPrefix = data.BillPrefix || data.billPrefix || 'INV';
            const finYear = data.FinYear || data.finYear || '';
            const billNo = `${billPrefix}/${String(billNumber)}/${finYear}`;
            setInvoice(prev => ({ ...prev, BillNumber: billNumber, BillPrefix: billPrefix, BillNo: billNo, FinYear: finYear }));
        } catch (e) { console.error('Error fetching rent invoice bill number:', e); }
    };

    const loadDeliveryData = async (id) => {
        setLoading(true);
        try {
            const res = await fetchData('DeliveryInApi', `/DeliveryInFromDeliveryOut/${companyId}/${id}`, token);
            const data = res?.response_data || res || {};
            const d = data.DeliveryCall || data.DeliveryInAndOut || data;
            const details = data.DeliveryCallDetails || data.DeliveryInAndOutDetails || [];
            setRawD(d);
            setRawDets(details);

            setDelivery({
                Id: d.Id || id,
                CompanyId: d.CompanyId || companyId,
                BranchId: d.BranchId || branchId,
                CustomerId: d.CustomerId || '',
                CustomerName: d.CustomerName || '',
                ContactNumber: d.ContactNumber || '',
                AddressLine: d.AddressLine || '',
                DeliveryDate: d.DeliveryDate ? d.DeliveryDate.split('T')[0] : '',
                DeliveryReturnDate: d.DeliveryReturnDate ? d.DeliveryReturnDate.split('T')[0] : today(),
                Remarks: d.Remarks || '',
                BillNumber: d.BillNumber || '',
                DeliveryNo: d.DeliveryNo || '',
                FinYear: d.FinYear || '',
                RentDays: d.RentDays || 0,
                DeliveryOutTime: d.DeliveryOutTime || '',
                DeliveryInTime: d.DeliveryInTime || getCurrentTime(),
                IsDeliveryInCompleted: d.IsDeliveryInCompleted || false,
                IsInvoiced: d.IsInvoiced || false,
                IsActive: d.IsActive || false,
                CreatedBy: d.CreatedBy || '',
                CreatedDate: d.CreatedDate | '',

                items: details.map(item => ({
                    Id: item.Id || '',
                    DeliveryId: item.DeliveryId || id,
                    ItemId: item.ItemId || '',
                    ItemName: item.ItemName || '',
                    DeliveryOutQuantity: item.DeliveryOutQuantity || 0,
                    DeliveryInQuantity: item.DeliveryInQuantity?.toString() || '0',
                    GoodQuantity: item.GoodQuantity?.toString() || '0',
                    DamagedQuantity: item.DamagedQuantity?.toString() || '0',
                    HSNCode: item.HSNCode || '',
                    GSTRate: item.GSTRate || 0,
                    Rate: item.Rate || 0,
                    RentRate: item.RentRate || item.Rate || 0,
                    IsActive: item.IsActive || false,
                    CreatedBy: item.CreatedBy || '',
                    CreatedDate: item.CreatedDate | '',
                }))
            });

            const rentDays = d.RentDays || 0;
            // Pre-fill invoice from delivery data
            setInvoice(prev => ({
                ...prev,
                DeliveryId: d.Id || id,
                CustomerId: d.CustomerId || '',
                CustomerName: d.CustomerName || '',
                ContactNumber: d.ContactNumber || '',
                AddressLine: d.AddressLine || '',
                RentDays: rentDays,
                AdvanceAmount: d.AdvanceAmount || 0,
                items: details.map(item => {
                    const rentRate = item.RentRate || item.Rate || 0;
                    const qty = item.DeliveryOutQuantity || 0;
                    const amount = qty * rentRate * rentDays;
                    return { Id: '', ItemId: item.ItemId || '', ItemName: item.ItemName || '', Quantity: qty, RentRate: rentRate, Amount: amount, TotalAmount: amount };
                })
            }));
        } catch (err) {
            console.error('Error loading delivery in data:', err);
        } finally {
            setLoading(false);
        }
    };

    // Auto-calc RentDays from DeliveryDate and DeliveryReturnDate
    useEffect(() => {
        if (delivery.DeliveryDate && delivery.DeliveryReturnDate) {
            const diff = Math.max(1, Math.ceil(Math.abs(new Date(delivery.DeliveryReturnDate) - new Date(delivery.DeliveryDate)) / 86400000));
            setDelivery(prev => ({ ...prev, RentDays: diff }));
            // Sync invoice RentDays and recalc item amounts
            setInvoice(prev => ({
                ...prev,
                RentDays: diff,
                items: prev.items.map(item => {
                    const amount = (parseFloat(item.Quantity) || 0) * (parseFloat(item.RentRate) || 0) * diff;
                    return { ...item, Amount: amount, TotalAmount: amount };
                })
            }));
        }
    }, [delivery.DeliveryDate, delivery.DeliveryReturnDate]);

    // Auto-calc invoice totals from delivery items
    useEffect(() => {
        const rentDays = parseFloat(delivery.RentDays) || 0;
        const amount = delivery.items.reduce((s, item) => {
            const inQty = parseFloat(item.DeliveryInQuantity) || 0;
            const rentRate = parseFloat(item.RentRate || item.Rate) || 0;
            return s + inQty * rentRate * rentDays;
        }, 0);
        const transport = parseFloat(invoice.TransportingCharge) || 0;
        const additional = parseFloat(invoice.AdditionalAmount) || 0;
        const roundOff = parseFloat(invoice.RoundOff) || 0;
        const total = amount + transport + additional + roundOff;
        const paid = parseFloat(invoice.PaidAmount) || 0;
        const advance = parseFloat(invoice.AdvanceAmount) || 0;
        const balance = total - paid - advance;
        setInvoice(prev => ({ ...prev, Amount: amount, NetAmount: amount, TotalAmount: total, BalanceAmount: balance }));
    }, [delivery.items, delivery.RentDays, invoice.TransportingCharge, invoice.AdditionalAmount, invoice.RoundOff, invoice.PaidAmount, invoice.AdvanceAmount]);

    const handleDeliveryChange = (field, value, idx = null) => {
        if (idx !== null) {
            setDelivery(prev => {
                const items = [...prev.items];
                items[idx] = { ...items[idx], [field]: value };
                if (field === 'GoodQuantity') {
                    const good = parseFloat(value) || 0;
                    const outQty = parseFloat(items[idx].DeliveryOutQuantity) || 0;
                    if (good > outQty) {
                        setErrorMessage(`Good Qty cannot exceed Out Qty (${outQty})`);
                        setShowError(true);
                        items[idx].GoodQuantity = '0';
                        items[idx].DamagedQuantity = outQty.toString();
                        items[idx].DeliveryInQuantity = '0';
                    } else {
                        items[idx].DamagedQuantity = (outQty - good).toString();
                        items[idx].DeliveryInQuantity = good.toString();
                    }
                } else if (field === 'DamagedQuantity') {
                    const damaged = parseFloat(value) || 0;
                    const outQty = parseFloat(items[idx].DeliveryOutQuantity) || 0;
                    if (damaged > outQty) {
                        setErrorMessage(`Damaged Qty cannot exceed Out Qty (${outQty})`);
                        setShowError(true);
                        items[idx].DamagedQuantity = '0';
                        items[idx].GoodQuantity = outQty.toString();
                        items[idx].DeliveryInQuantity = outQty.toString();
                    } else {
                        items[idx].GoodQuantity = (outQty - damaged).toString();
                        items[idx].DeliveryInQuantity = (outQty - damaged).toString();
                    }
                }
                return { ...prev, items };
            });
        } else {
            setDelivery(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleInvoiceChange = (field, value, idx = null) => {
        if (idx !== null) {
            setInvoice(prev => {
                const items = [...prev.items];
                items[idx] = { ...items[idx], [field]: value };
                if (field === 'Quantity' || field === 'RentRate') {
                    const qty = parseFloat(field === 'Quantity' ? value : items[idx].Quantity) || 0;
                    const rentRate = parseFloat(field === 'RentRate' ? value : items[idx].RentRate) || 0;
                    const rentDays = parseFloat(prev.RentDays) || 0;
                    const amount = qty * rentRate * rentDays;
                    items[idx].Amount = amount;
                    items[idx].TotalAmount = amount;
                }
                return { ...prev, items };
            });
        } else {
            setInvoice(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleSave = async () => {
        if (!delivery.DeliveryInTime) {
            setErrors({ DeliveryInTime: 'Required' });
            return;
        }
        setLoading(true);
        try {
            const deliveryOutVM = {
                DeliveryInAndOut: {
                    ...rawD,
                    DeliveryReturnDate: delivery.DeliveryReturnDate || null,
                    Remarks: delivery.Remarks || null,
                    RentDays: parseFloat(delivery.RentDays) || 0,
                    DeliveryInTime: delivery.DeliveryInTime || null,
                    IsDeliveryInCompleted: true,
                    ModifiedBy: parseInt(userId),
                    DeliveryInCreatedBy: parseInt(userId),
                    DeliveryInModifiedBy: parseInt(userId),
                    DeliveryInCreatedDate: new Date().toISOString(),
                    DeliveryInModifiedDate: new Date().toISOString(),
                },
                DeliveryInAndOutDetails: delivery.items.map((item, idx) => ({
                    ...(rawDets[idx] || {}),
                    Id: item.Id || undefined,
                    DeliveryId: delivery.Id,
                    ItemId: item.ItemId,
                    ItemName: item.ItemName,
                    DeliveryOutQuantity: parseFloat(item.DeliveryOutQuantity) || 0,
                    DeliveryInQuantity: parseFloat(item.DeliveryInQuantity) || 0,
                    GoodQuantity: parseFloat(item.GoodQuantity) || 0,
                    DamagedQuantity: parseFloat(item.DamagedQuantity) || 0,
                    Rate: parseFloat(item.RentRate || item.Rate) || 0,
                    ModifiedBy: parseInt(userId),
                    DeliveryInCreatedBy: parseInt(userId),
                    DeliveryInModifiedBy: parseInt(userId),
                    DeliveryInCreatedDate: new Date().toISOString(),
                    DeliveryInModifiedDate: new Date().toISOString(),
                }))
            };

            const res = await updateData('DeliveryInApi', delivery.Id, deliveryOutVM, token);
            const ok = res?.responseCode === '1111' || res?.responseCode === 1111 || res?.success === true;
            if (ok) {
                // Save RentInvoice if there are items
                if (delivery.items.length > 0) {
                    await saveRentInvoice();
                }
                setSuccessMessage('Delivery In saved successfully!');
                setShowSuccess(true);
                setTimeout(() => { onSave(); onClose(); }, 1500);
            }
        } catch (err) {
            console.error('Error saving delivery in:', err);
        } finally {
            setLoading(false);
        }
    };

    const saveRentInvoice = async () => {
        const rentDays = parseFloat(delivery.RentDays) || 0;
        const invoiceItems = delivery.items.map(item => {
            const rentRate = parseFloat(item.RentRate || item.Rate) || 0;
            const qty = parseFloat(item.DeliveryInQuantity) || 0;
            const amount = qty * rentRate * rentDays;
            return {
                Id: undefined,
                ItemId: item.ItemId,
                ItemName: item.ItemName,
                Quantity: qty,
                Rate: rentRate,
                Amount: amount,
                DiscountPercentage: 0,
                DiscountAmount: 0,
                HSNCode: item.HSNCode,
                GSTRate: 0,
                TaxableAmount: amount,
                CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0,
                TotalAmount: amount,
                CreatedBy: parseInt(userId),
                ModifiedBy: parseInt(userId),
                IsActive: true,

            };
        });
        const invoicePayload = {
            RentInvoice: {
                Id: invoice.Id || undefined,
                CompanyId: parseInt(companyId),
                BranchId: parseInt(branchId),
                DeliveryId: delivery.Id,
                CustomerId: invoice.CustomerId || null,
                CustomerName: invoice.CustomerName,
                ContactNumber: invoice.ContactNumber || null,
                AddressLine: invoice.AddressLine || null,
                BillNumber: invoice.BillNumber || 0,
                FinYear: invoice.FinYear || delivery.FinYear || '',
                BillDate: invoice.BillDate || null,
                Amount: parseFloat(invoice.Amount) || 0,
                DiscountAmount: 0,
                NetAmount: parseFloat(invoice.NetAmount) || 0,
                CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0,
                RoundOff: parseFloat(invoice.RoundOff) || 0,
                TransportingCharge: parseFloat(invoice.TransportingCharge) || 0,
                TotalAmount: parseFloat(invoice.TotalAmount) || 0,
                AdvanceAmount: parseFloat(invoice.AdvanceAmount) || 0,
                PaidAmount: parseFloat(invoice.PaidAmount) || 0,
                BalanceAmount: parseFloat(invoice.BalanceAmount) || 0,
                ModeOfPayment: parseInt(invoice.ModeOfPayment) || 0,
                Remarks: invoice.Remarks || null,
                RentDays: rentDays,
                AdditionalAmount: parseFloat(invoice.AdditionalAmount) || 0,
                AdditionalRemarks: invoice.AdditionalRemarks || null,
                CreatedBy: parseInt(userId),
                ModifiedBy: parseInt(userId),
                IsActive: true,
                DeliveryInCreatedDate: new Date().toISOString(),
                DeliveryOutDate: delivery.DeliveryDate || null,
            },
            RentInvoiceDetails: invoiceItems
        };
        try {
            if (invoice.Id) {
                await updateData('RentInvoiceApi', invoice.Id, invoicePayload, token);
            } else {
                await postData('RentInvoiceApi', invoicePayload, token);
            }
        } catch (err) {
            console.error('Error saving rent invoice:', err);
        }
    };

    if (!open) return null;

    const fSx = {
        '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
        '& .MuiInputLabel-root': { fontSize: '0.80rem' }
    };
    const numSx = {
        '& .MuiInputBase-input': { fontSize: '0.75rem', textAlign: 'right', py: 0.4 },
        '& .MuiInputBase-root': { height: '26px' }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper"
            PaperProps={{ sx: { borderRadius: 2, maxHeight: '92vh', width: '80vw', maxWidth: '1000px' } }}>
            <DialogTitle sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', p: 1, height: '35px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 24, height: 24 }}>
                            <DeliveryInIcon sx={{ fontSize: 14 }} />
                        </Avatar>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                            {isView ? 'View Delivery In' : 'Delivery In'}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: 'white', p: 0.3 }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 1.5, pt: 2.5, overflowY: 'auto' }}>
                {loading && <LinearProgress sx={{ mb: 1 }} />}

                {/* ── Delivery Info ── */}
                <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" label="Delivery No"
                            value={delivery.DeliveryNo} InputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" label="Delivery Date" type="date"
                            value={delivery.DeliveryDate} InputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" label="Return Date" type="date"
                            value={delivery.DeliveryReturnDate}
                            onChange={(e) => handleDeliveryChange('DeliveryReturnDate', e.target.value)}
                            InputProps={{ readOnly: isView }} InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>
                    <Grid item xs={12} md={1.6}>
                        <TextField fullWidth size="small" label="Rent Days" type="number"
                            value={delivery.RentDays}
                            onChange={(e) => handleDeliveryChange('RentDays', e.target.value)}
                            InputProps={{ readOnly: isView }} InputLabelProps={{ shrink: true }}
                            sx={{ ...fSx, '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem', bgcolor: '#fef3c7' }, '& .MuiInputBase-input': { textAlign: 'right' } }} />
                    </Grid>
                    <Grid item xs={12} md={2.2}>
                        <TextField fullWidth size="small" label="Out Time"
                            value={delivery.DeliveryOutTime} InputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>
                    <Grid item xs={12} md={2.2}>
                        <TextField fullWidth size="small" label="In Time *" type="time"
                            value={delivery.DeliveryInTime}
                            onChange={(e) => handleDeliveryChange('DeliveryInTime', e.target.value)}
                            error={!!errors.DeliveryInTime} helperText={errors.DeliveryInTime}
                            InputProps={{ readOnly: isView }} InputLabelProps={{ shrink: true }}
                            sx={{ ...fSx, '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.2 } }} />
                    </Grid>

                    {/* Customer row */}
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth size="small" label="Customer Name"
                            value={delivery.CustomerName} InputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" label="Contact"
                            value={delivery.ContactNumber} InputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth size="small" label="Address"
                            value={delivery.AddressLine} InputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth size="small" label="Remarks"
                            value={delivery.Remarks}
                            onChange={(e) => handleDeliveryChange('Remarks', e.target.value)}
                            InputProps={{ readOnly: isView }} InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>

                    {/* Combined Items + Invoice table */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                            <Box sx={{ bgcolor: '#f0fdf4', px: 1.5, py: 0.8, borderBottom: '1px solid #e2e8f0' }}>
                                <Typography sx={{ fontWeight: 600, color: '#065f46', fontSize: '0.75rem' }}>Delivery Items</Typography>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#f9fafb' }}>
                                            {['Item Name', 'Out Qty', 'Good Qty', 'Damaged Qty', 'In Qty', 'Rent Days', 'Rent Rate', 'Amount'].map(h => (
                                                <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', py: 1, px: 1.5, textAlign: h === 'Item Name' ? 'left' : 'right' }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {delivery.items.map((item, idx) => {
                                            const rentRate = parseFloat(item.RentRate || item.Rate) || 0;
                                            const inQty = parseFloat(item.DeliveryInQuantity) || 0;
                                            const rentDays = parseFloat(delivery.RentDays) || 0;
                                            const amount = inQty * rentRate * rentDays;
                                            return (
                                                <TableRow key={idx} sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                                                    <TableCell sx={{ py: 0.8, px: 1.5, fontSize: '0.78rem', fontWeight: 500 }}>{item.ItemName}</TableCell>
                                                    <TableCell sx={{ py: 0.8, px: 1.5, textAlign: 'right' }}>
                                                        <Typography sx={{ fontSize: '0.75rem' }}>{item.DeliveryOutQuantity}</Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.8, px: 1.5, textAlign: 'right' }}>
                                                        <TextField type="number" value={item.GoodQuantity} size="small"
                                                            onChange={(e) => handleDeliveryChange('GoodQuantity', e.target.value, idx)}
                                                            InputProps={{ readOnly: isView }} sx={{ width: '75px', ...numSx }} />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.8, px: 1.5, textAlign: 'right' }}>
                                                        <TextField type="number" value={item.DamagedQuantity} size="small"
                                                            onChange={(e) => handleDeliveryChange('DamagedQuantity', e.target.value, idx)}
                                                            InputProps={{ readOnly: isView }} sx={{ width: '75px', ...numSx }} />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.8, px: 1.5, textAlign: 'right' }}>
                                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{item.DeliveryInQuantity}</Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.8, px: 1.5, textAlign: 'right' }}>
                                                        <Typography sx={{ fontSize: '0.75rem', color: '#7c3aed', fontWeight: 600 }}>{rentDays}</Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.8, px: 1.5, textAlign: 'right' }}>
                                                        <TextField type="number" value={rentRate} size="small"
                                                            onChange={(e) => handleDeliveryChange('RentRate', e.target.value, idx)}
                                                            InputProps={{ readOnly: isView }} sx={{ width: '80px', ...numSx }} />
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.8, px: 1.5, textAlign: 'right' }}>
                                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1e40af' }}>{amount.toFixed(2)}</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>

                {/* ── Rent Invoice Section ── */}
                {!isView && <>
                <Divider sx={{ my: 2, borderColor: '#e2e8f0' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: '#7c3aed', width: 22, height: 22 }}>
                        <InvoiceIcon sx={{ fontSize: 13 }} />
                    </Avatar>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#2d3748' }}>Rent Invoice</Typography>
                </Box>

                <Grid container spacing={1.5}>
                    <Grid item xs={6} md={2}>
                        <TextField fullWidth size="small" label="Bill No"
                            value={invoice.BillNo || invoice.BillNumber}
                            InputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ ...fSx, '& .MuiInputBase-input': { textAlign: 'left' } }} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField fullWidth size="small" label="Bill Date" type="date"
                            value={invoice.BillDate}
                            onChange={(e) => handleInvoiceChange('BillDate', e.target.value)}
                            InputProps={{ readOnly: isView }} InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>

                    <Grid item xs={6} md={2}>
                        <TextField fullWidth size="small" label="Round Off" type="number"
                            value={invoice.RoundOff}
                            onChange={(e) => handleInvoiceChange('RoundOff', e.target.value)}
                            InputProps={{ readOnly: isView }} InputLabelProps={{ shrink: true }}
                            sx={{ ...fSx, '& .MuiInputBase-input': { textAlign: 'right' } }} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField fullWidth size="small" label="Mode of Payment" select
                            value={invoice.ModeOfPayment}
                            onChange={(e) => handleInvoiceChange('ModeOfPayment', e.target.value)}
                            InputProps={{ readOnly: isView }} InputLabelProps={{ shrink: true }} sx={fSx}>
                            <MenuItem value={0} sx={{ fontSize: '0.78rem' }}>Cash</MenuItem>
                            <MenuItem value={1} sx={{ fontSize: '0.78rem' }}>Cheque</MenuItem>
                            <MenuItem value={2} sx={{ fontSize: '0.78rem' }}>Online</MenuItem>
                            <MenuItem value={3} sx={{ fontSize: '0.78rem' }}>Card</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField fullWidth size="small" label="Advance Amount" type="number"
                            value={invoice.AdvanceAmount}
                            onChange={(e) => handleInvoiceChange('AdvanceAmount', e.target.value)}
                            InputProps={{ readOnly: isView }} InputLabelProps={{ shrink: true }}
                            sx={{ ...fSx, '& .MuiInputBase-input': { textAlign: 'right' } }} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField fullWidth size="small" label="Paid Amount" type="number"
                            value={invoice.PaidAmount}
                            onChange={(e) => handleInvoiceChange('PaidAmount', e.target.value)}
                            InputProps={{ readOnly: isView }} InputLabelProps={{ shrink: true }}
                            sx={{ ...fSx, '& .MuiInputBase-input': { textAlign: 'right' } }} />
                    </Grid>
                    {delivery.items.some(i => parseFloat(i.DamagedQuantity) > 0) && (<>
                        <Grid item xs={6} md={2}>
                            <TextField fullWidth size="small" label="Additional Amount" type="number"
                                value={invoice.AdditionalAmount}
                                onChange={(e) => handleInvoiceChange('AdditionalAmount', e.target.value)}
                                InputProps={{ readOnly: isView }} InputLabelProps={{ shrink: true }}
                                sx={{ ...fSx, '& .MuiInputBase-input': { textAlign: 'right' } }} />
                        </Grid>
                        <Grid item xs={6} md={2.5}>
                            <TextField fullWidth size="small" label="Additional Remarks"
                                value={invoice.AdditionalRemarks}
                                onChange={(e) => handleInvoiceChange('AdditionalRemarks', e.target.value)}
                                InputProps={{ readOnly: isView }} InputLabelProps={{ shrink: true }} sx={fSx} />
                        </Grid>
                    </>)}
                    <Grid item xs={6} md={2}>
                        <TextField fullWidth size="small" label="Total"
                            value={(parseFloat(invoice.TotalAmount) || 0).toFixed(2)}
                            InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }}
                            sx={{ ...fSx, '& .MuiInputBase-input': { textAlign: 'right' } }} />                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField fullWidth size="small" label="Balance"
                            value={(parseFloat(invoice.BalanceAmount) || 0).toFixed(2)}
                            InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }}
                            sx={{ ...fSx, '& .MuiInputBase-input': { textAlign: 'right' } }} />                    </Grid>
                    <Grid item xs={12} md={3.5}>
                        <TextField fullWidth size="small" label="Remarks"
                            value={invoice.Remarks}
                            onChange={(e) => handleInvoiceChange('Remarks', e.target.value)}
                            InputProps={{ readOnly: isView }} InputLabelProps={{ shrink: true }} sx={fSx} />
                    </Grid>
                </Grid>
                </>}
            </DialogContent>

            <DialogActions sx={{ p: 1, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" disabled={loading}
                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', py: 0.4, px: 1.5 }}>
                    {isView ? 'Close' : 'Cancel'}
                </Button>
                {!isView && (
                    <Button onClick={handleSave} variant="contained" disabled={loading}
                        sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', py: 0.4, px: 1.5 }}>
                        {loading ? 'Saving...' : 'Save'}
                    </Button>
                )}
            </DialogActions>

            <Snackbar open={showSuccess} autoHideDuration={1500} onClose={() => setShowSuccess(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="success" sx={{ width: '100%', fontSize: '0.9rem', fontWeight: 600 }}>
                    {successMessage}
                </Alert>
            </Snackbar>
            <Snackbar open={showError} autoHideDuration={3000} onClose={() => setShowError(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" sx={{ width: '100%', fontSize: '0.9rem', fontWeight: 600 }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

export default DeliveryInModal;
